/* eslint-disable max-lines */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useChat } from '@/components/providers/ChatProvider';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  deleteHcpCloudToken,
  type HCPCloudToken,
  terraformBackendHcpCloudQueries,
  upsertHCPCloudToken,
} from '@/lib/queries/integrations/terraformBackend/hcpCloud';
import type { Project } from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';

import {
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

interface Props {
  initialValue?: HCPCloudToken;
  mainProject: Project;
}

export const HCPCloudTokenForm = ({
  initialValue,
  mainProject,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const { setPrompt } = useChat();
  const router = useRouter();
  const { toast, dismiss } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ...defaultValues,
      ...initialValue,
    },
  });

  const { isPending, mutateAsync } = useMutation({
    mutationKey: generateMutationKey(
      terraformBackendHcpCloudQueries.byProject(mainProject.id),
      'upsert',
      true,
    ),
    mutationFn: async (values: FormValues) => {
      await upsertHCPCloudToken(mainProject.id, values, initialValue?.id);
    },
    onSuccess: async () => {
      toast({
        title:
          initialValue !== undefined
            ? 'integrations.terraformBackend.hcpCloud.form.update.success'
            : 'integrations.terraformBackend.hcpCloud.form.create.success',
        variant: 'default',
        description: 'integrations.terraformBackend.hcpCloud.tryIt',
        descriptionValues: {
          button: parts => (
            <>
              <br />
              <Button
                variant="main"
                className="mt-2"
                onClick={() => {
                  dismiss();
                  setPrompt(
                    'List IAM users with AdministratorAccess policy attached in my IAC',
                  );
                }}
              >
                {parts}
              </Button>
            </>
          ),
        },
      });
      router.navigate({ to: '/integrations/terraform-backend/hcp-cloud' });
    },
    onError: () => {
      toast({
        title:
          initialValue !== undefined
            ? 'integrations.terraformBackend.hcpCloud.form.update.error'
            : 'integrations.terraformBackend.hcpCloud.form.create.error',
        variant: 'destructive',
      });
    },
  });

  const { isPending: isDeleting, mutateAsync: deleteMutation } = useMutation({
    mutationKey: generateMutationKey(
      terraformBackendHcpCloudQueries.list(mainProject.id),
      'delete',
      true,
    ),
    mutationFn: async (tokenId: string) => {
      await deleteHcpCloudToken(mainProject.id, tokenId);
    },
    onSuccess: async () => {
      toast({
        title: 'integrations.terraformBackend.hcpCloud.form.delete.success',
        variant: 'default',
      });
      router.navigate({ to: '/integrations/terraform-backend/hcp-cloud' });
    },
    onError: () => {
      toast({
        title: 'integrations.terraformBackend.hcpCloud.form.delete.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(async values => mutateAsync(values))}>
        <h1 className="text-4xl mb-3">
          <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.title" />
        </h1>
        <h3 className="text-lg mb-6">
          <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.subtitle" />
        </h3>
        <div className="flex flex-col gap-4 md:w-[40%] mb-5">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.name.label" />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    type="text"
                    placeholder={formatMessage({
                      id: 'integrations.terraformBackend.hcpCloud.form.name.placeholder',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.url.label" />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    type="text"
                    placeholder={formatMessage({
                      id: 'integrations.terraformBackend.hcpCloud.form.url.placeholder',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="workspace"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.workspace.label" />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    type="text"
                    placeholder={formatMessage({
                      id: 'integrations.terraformBackend.hcpCloud.form.workspace.placeholder',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="token"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.token.label" />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    type="password"
                    placeholder={formatMessage({
                      id: 'integrations.terraformBackend.hcpCloud.form.token.placeholder',
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-row gap-4 justify-between">
            {initialValue !== undefined ? (
              <>
                <Button variant="main" disabled={isPending} type="submit">
                  {isPending ? (
                    <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.update.pending" />
                  ) : (
                    <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.update.label" />
                  )}
                </Button>
                <Button
                  variant="destructive"
                  className="bg-red-500 hover:bg-red-600"
                  disabled={isDeleting}
                  onClick={() => {
                    deleteMutation(initialValue.id);
                  }}
                  type="button"
                >
                  {isDeleting ? (
                    <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.delete.pending" />
                  ) : (
                    <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.delete.label" />
                  )}
                </Button>
              </>
            ) : (
              <Button variant="main" disabled={isPending} type="submit">
                {isPending ? (
                  <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.create.pending" />
                ) : (
                  <FormattedMessage id="integrations.terraformBackend.hcpCloud.form.create.label" />
                )}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Form>
  );
};
